import { ContentBlock, ContentState, EditorState, genKey } from 'draft-js';
import { parseInlineLineHeight } from '../../elements/text/utils/mixedTypography';
import { LINE_HEIGHT } from './customConstants';

export const EMPTY_LINE_HEIGHT_DATA = 'stEmptyLineHeight';

export function getExplicitEmptyLineHeight(block) {
  return block.getLength() === 0
    ? parseInlineLineHeight(block.getData().get(EMPTY_LINE_HEIGHT_DATA))
    : null;
}

// Deleting a paragraph's last text must move its custom height from characters
// to blank metadata. Amend the deletion itself so undo remains a single step.
export function preserveDeletedLineHeight(previousState, nextState) {
  if (
    !previousState ||
    !nextState ||
    !['backspace-character', 'delete-character', 'remove-range'].includes(
      nextState.getLastChangeType()
    ) ||
    !nextState.getSelection().isCollapsed()
  ) {
    return nextState;
  }
  const previousContent = previousState.getCurrentContent();
  const content = nextState.getCurrentContent();
  if (previousContent === content) {
    return nextState;
  }
  const key = nextState.getSelection().getStartKey();
  const previousBlock = previousContent.getBlockForKey(key);
  const block = content.getBlockForKey(key);
  // Empty-height serialization is supported for ordinary paragraphs only.
  if (
    !previousBlock ||
    previousBlock.getLength() === 0 ||
    block.getLength() !== 0 ||
    block.getType() !== 'unstyled'
  ) {
    return nextState;
  }
  // Paragraph joins/removals have different inheritance semantics.
  if (
    !previousContent
      .getBlockMap()
      .keySeq()
      .equals(content.getBlockMap().keySeq())
  ) {
    return nextState;
  }
  const style = previousBlock
    .getInlineStyleAt(previousBlock.getLength() - 1)
    .find((item) => item.startsWith(`${LINE_HEIGHT}-`));
  const height = style
    ? parseInlineLineHeight(style.slice(LINE_HEIGHT.length + 1))
    : null;
  // Use the current text style, not stale metadata from a previously empty line.
  const data =
    height === null
      ? block.getData().remove(EMPTY_LINE_HEIGHT_DATA)
      : block.getData().set(EMPTY_LINE_HEIGHT_DATA, height);
  if (data === block.getData()) {
    return nextState;
  }
  return EditorState.set(nextState, {
    currentContent: content.setIn(['blockMap', key, 'data'], data),
    nativelyRenderedContent: null,
  });
}

export function isBlockSelected(content, selection, block) {
  const blocks = content.getBlocksAsArray();
  const index = blocks.indexOf(block);
  const start = blocks.findIndex(
    (item) => item.getKey() === selection.getStartKey()
  );
  const end = blocks.findIndex(
    (item) => item.getKey() === selection.getEndKey()
  );
  if (index < start || index > end) {
    return false;
  }
  // A selection ending at the next paragraph's start excludes that paragraph.
  // Select-all still includes a final empty paragraph.
  const all =
    selection.getStartKey() === content.getFirstBlock().getKey() &&
    selection.getStartOffset() === 0 &&
    selection.getEndKey() === content.getLastBlock().getKey() &&
    selection.getEndOffset() === content.getLastBlock().getLength();
  return (
    selection.isCollapsed() ||
    index < end ||
    selection.getEndOffset() > 0 ||
    all
  );
}

export function getEmptyLineHeightAtCaret(editorState, defaultLineHeight) {
  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) {
    return null;
  }
  const content = editorState.getCurrentContent();
  const target = content.getBlockForKey(selection.getStartKey());
  const offset = selection.getStartOffset();
  const text = target.getText();
  if (
    (offset > 0 && text[offset - 1] !== '\n') ||
    (offset < text.length && text[offset] !== '\n')
  ) {
    return null;
  }
  let value = defaultLineHeight;
  for (const block of content.getBlocksAsArray()) {
    const explicit = getExplicitEmptyLineHeight(block);
    if (explicit !== null) {
      value = explicit;
    }
    const end = block === target ? offset : block.getLength();
    for (let i = 0; i < end; i++) {
      if (block.getText()[i] !== '\n') {
        const style = block
          .getInlineStyleAt(i)
          .find((item) => item.startsWith(`${LINE_HEIGHT}-`));
        value = style
          ? parseInlineLineHeight(style.slice(LINE_HEIGHT.length + 1)) ??
            defaultLineHeight
          : defaultLineHeight;
      }
    }
    if (block === target) {
      return value;
    }
  }
  return null;
}

/**
 * An empty soft line has no character at the end of a block. When explicitly
 * formatting one, normalize that block's soft breaks to Draft paragraphs (the
 * same representation used on save/reopen). Keep text, character styles and
 * selection positions; no invisible characters or offset-indexed metadata.
 */
export function normalizeSelectedEmptyLines(editorState) {
  const content = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const mappings = new Map();
  const blocks = content.getBlocksAsArray().flatMap((block) => {
    const text = block.getText();
    if (
      !text.includes('\n') ||
      block.getType() !== 'unstyled' ||
      !isBlockSelected(content, selection, block)
    ) {
      return [block];
    }
    const start =
      block.getKey() === selection.getStartKey()
        ? selection.getStartOffset()
        : 0;
    const end =
      block.getKey() === selection.getEndKey()
        ? selection.getEndOffset()
        : text.length;
    let position = 0;
    const lines = text.split('\n').map((line) => {
      const result = { text: line, start: position };
      position += line.length + 1;
      return result;
    });
    if (
      !lines.some(
        (line) =>
          line.text === '' &&
          line.start >= start &&
          (selection.isCollapsed()
            ? line.start === end
            : line.start < end || end === text.length)
      )
    ) {
      return [block];
    }
    const normalized = lines.map((line, index) => ({
      ...line,
      key: index === 0 ? block.getKey() : genKey(),
    }));
    mappings.set(block.getKey(), normalized);
    return normalized.map(
      (line) =>
        new ContentBlock({
          key: line.key,
          type: block.getType(),
          depth: block.getDepth(),
          text: line.text,
          characterList: block
            .getCharacterList()
            .slice(line.start, line.start + line.text.length),
          data: block.getData().remove(EMPTY_LINE_HEIGHT_DATA),
        })
    );
  });
  if (mappings.size === 0) {
    return editorState;
  }
  const mapPoint = (key, offset) => {
    const lines = mappings.get(key);
    if (!lines) {
      return { key, offset };
    }
    const line = [...lines].reverse().find((item) => item.start <= offset);
    return { key: line.key, offset: offset - line.start };
  };
  const anchor = mapPoint(
    selection.getAnchorKey(),
    selection.getAnchorOffset()
  );
  const focus = mapPoint(selection.getFocusKey(), selection.getFocusOffset());
  const nextSelection = selection.merge({
    anchorKey: anchor.key,
    anchorOffset: anchor.offset,
    focusKey: focus.key,
    focusOffset: focus.offset,
  });
  return EditorState.set(editorState, {
    currentContent: content.merge({
      blockMap: ContentState.createFromBlockArray(blocks).getBlockMap(),
      selectionAfter: nextSelection,
    }),
    selection: nextSelection,
  });
}

export function setEmptyLineHeights(editorState, lineHeight) {
  const content = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const blockMap = content.getBlockMap().map((block) => {
    if (
      block.getLength() ||
      block.getType() !== 'unstyled' ||
      !isBlockSelected(content, selection, block)
    ) {
      return block;
    }
    const data =
      lineHeight === null
        ? block.getData().remove(EMPTY_LINE_HEIGHT_DATA)
        : block.getData().set(EMPTY_LINE_HEIGHT_DATA, lineHeight);
    return block.set('data', data);
  });
  return content.set('blockMap', blockMap);
}

export function getSelectedEmptyLineHeights(editorState, defaultLineHeight) {
  const normalized = normalizeSelectedEmptyLines(editorState);
  const content = normalized.getCurrentContent();
  const selection = normalized.getSelection();
  return content
    .getBlocksAsArray()
    .filter(
      (block) =>
        block.getLength() === 0 &&
        block.getType() === 'unstyled' &&
        isBlockSelected(content, selection, block)
    )
    .map((block) =>
      getEmptyLineHeightAtCaret(
        EditorState.acceptSelection(
          normalized,
          selection.merge({
            anchorKey: block.getKey(),
            focusKey: block.getKey(),
            anchorOffset: 0,
            focusOffset: 0,
          })
        ),
        defaultLineHeight
      )
    );
}
